import { Box, Field, Flex, HStack, Input, InputGroup, Slider, Text } from "@chakra-ui/react";

import { clamp, parseDigits as defaultParseDigits } from "./_useRangeDraft";

// Shared presentation body for min/max range filters. Pair with
// `useRangeDraft` for state. Caller controls formatting + placeholders.
//
// Bug-fix history baked in here so it can't drift between range filters:
//   1. Input handlers clamp ONLY to absolute bounds — not to the other
//      field's current value. Previously typing "1" in max with min=900
//      would snap to "900" every keystroke (clamp(n, draftMin, bounds.max)
//      forced max up to draftMin). Lets the user type any value freely;
//      order normalization happens in commit() in the hook.
//   2. Slider value is `[Math.min(min, max), Math.max(min, max)]`-sorted
//      so an inverted-during-typing draft doesn't bug out the slider
//      thumbs.
//   3. (in the hook) commit() normalizes min/max order so user-typed
//      inversions still apply correctly on Aplicar.
export const RangeBody = ({
    draft,
    // (value, atMax) => string for slider min/max labels under the track
    formatLabel,
    // (value, isEmpty) => string for the input fields
    formatInputValue,
    // (rawString) => number | null  (defaults to digits-only)
    parseDigits = defaultParseDigits,
    placeholders = { min: "Sin mínimo", max: "Sin máximo" },
    inputHeight = "40px",
}) => {
    const { minDraft, maxDraft, setMinDraft, setMaxDraft, bounds, isMaxAtTop } = draft;

    const onSliderChange = ({ value }) => {
        const [nextMin, nextMax] = value;
        setMinDraft(nextMin);
        setMaxDraft(nextMax);
    };

    const onMinInputChange = (e) => {
        const n = parseDigits(e.target.value);
        if (n === null) {
            setMinDraft(bounds.min);
            return;
        }
        if (!Number.isFinite(n)) return;
        setMinDraft(clamp(n, bounds.min, bounds.max));
    };

    const onMaxInputChange = (e) => {
        const n = parseDigits(e.target.value);
        if (n === null) {
            setMaxDraft(bounds.max);
            return;
        }
        if (!Number.isFinite(n)) return;
        setMaxDraft(clamp(n, bounds.min, bounds.max));
    };

    return (
        <Box>
            <Box px={2} pt={2} pb={2}>
                <Slider.Root
                    value={[
                        clamp(Math.min(minDraft, maxDraft), bounds.min, bounds.max),
                        clamp(Math.max(minDraft, maxDraft), bounds.min, bounds.max),
                    ]}
                    min={bounds.min}
                    max={bounds.max}
                    step={bounds.step}
                    onValueChange={onSliderChange}
                >
                    <Slider.Control>
                        <Slider.Track bg="gray.200">
                            <Slider.Range bg="colorPalette.solid" />
                        </Slider.Track>
                        <Slider.Thumbs cursor="grab" />
                    </Slider.Control>
                </Slider.Root>
                <Flex justify="space-between" mt={2}>
                    <Text fontSize="xs" color="gray.500">
                        {formatLabel(minDraft, false)}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {formatLabel(maxDraft, isMaxAtTop)}
                    </Text>
                </Flex>
            </Box>
            <HStack gap={3} mt={3}>
                <Field.Root>
                    <Field.Label fontSize="xs" color="gray.600">
                        Mínimo
                    </Field.Label>
                    <InputGroup>
                        <Input
                            type="text"
                            inputMode="numeric"
                            value={formatInputValue(minDraft, minDraft === bounds.min)}
                            onChange={onMinInputChange}
                            placeholder={placeholders.min}
                            fontSize="sm"
                            h={inputHeight}
                            px={3}
                        />
                    </InputGroup>
                </Field.Root>
                <Field.Root>
                    <Field.Label fontSize="xs" color="gray.600">
                        Máximo
                    </Field.Label>
                    <InputGroup>
                        <Input
                            type="text"
                            inputMode="numeric"
                            value={formatInputValue(maxDraft, isMaxAtTop)}
                            onChange={onMaxInputChange}
                            placeholder={placeholders.max}
                            fontSize="sm"
                            h={inputHeight}
                            px={3}
                        />
                    </InputGroup>
                </Field.Root>
            </HStack>
        </Box>
    );
};
